import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../application/provider';
import { DonutChart } from '@tremor/react';
import UsuariosService from '../axios_services/usuarios.service';
import Loader from '../components/Loader/Loader';
import { ButtonSearch } from '../components/Buttons/Buttons';
import './ReporteColaboradores.css';

const colors = ["blue", "cyan", "indigo", "violet", "purple", "fuchsia", "pink", "rose", "orange", "amber", "yellow", "lime", "green", "emerald", "teal", "sky"];

const tailwindColorMap = {
    blue: "#3b82f6", cyan: "#06b6d4", indigo: "#6366f1", violet: "#8b5cf6",
    purple: "#a855f7", fuchsia: "#d946ef", pink: "#ec4899", rose: "#f43f5e",
    orange: "#f97316", amber: "#f59e0b", yellow: "#eab308", lime: "#84cc16",
    green: "#22c55e", emerald: "#10b981", teal: "#14b8a6", sky: "#0ea5e9"
};

const CustomTooltip = ({ payload, active }) => {
    if (!active || !payload || payload.length === 0) return null;
    const categoryPayload = payload[0];
    const data = categoryPayload.payload;
    return (
        <div className="custom-tooltip" style={{ width: '300px', zIndex: 1000 }}>
            <div className="tooltip-content">
                <div className="color-indicator" style={{ backgroundColor: tailwindColorMap[categoryPayload.color] || categoryPayload.color }} />
                <div className="tooltip-info">
                    <div className="tooltip-row" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                        <p className="tooltip-name" style={{ whiteSpace: 'normal', marginBottom: '4px' }}>{data.name}</p>
                        <p className="tooltip-value" style={{ whiteSpace: 'nowrap', fontWeight: '700' }}>
                            {data.total} {data.total === 1 ? 'Colaborador' : 'Colaboradores'}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ReporteColaboradores = () => {
    const [state] = useContext(AppContext);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [area, setArea] = useState('0');
    const [estado, setEstado] = useState('activos');
    const [periodo, setPeriodo] = useState(new Date().toISOString().slice(0, 7));

    const perfiles = state.catalogos.perfiles || [];

    const MACRO_AREAS = [
        { id: '1', label: 'ADMINISTRACIÓN' },
        { id: '2', label: 'NEGOCIOS' },
        { id: '3', label: 'EXTERNO' },
        { id: '4', label: 'GERENCIA' },
    ];


    useEffect(() => {
        if (perfiles.length > 0) {
            fetchData();
        }
    }, [perfiles]);

    const fetchData = async () => {
        if (perfiles.length === 0) return;
        setLoading(true);
        try {
            const res = await UsuariosService.getReporteColaboradores(area, periodo, estado);
            const chartData = res.map(item => {
                const perfil = perfiles.find(p => String(p.perfil_id) === String(item.perfil_id));
                return {
                    name: perfil ? perfil.descripcion : `Perfil ${item.perfil_id}`,
                    total: Number(item.total)
                };
            });
            setData(chartData);
        } catch (err) {
            console.error("Error fetching report data:", err);
        }
        setLoading(false);
    };

    const totalColaboradores = data.reduce((acc, curr) => acc + curr.total, 0);

    return (
        <div className="caja-flujo-container reporte-colaboradores-module">
            {loading && <Loader />}
            
            <div className="flex items-center justify-between mb-4">
                <h1>REPORTE DE COLABORADORES</h1>
            </div>

            <div className="filtros" style={{ marginBottom: '10px' }}>
                <div>
                    <label>Periodo:</label>
                    <input
                        type="month"
                        value={periodo}
                        onChange={(e) => setPeriodo(e.target.value)}
                    />
                </div>
                <div>
                    <label>Área:</label>
                    <select
                        value={area}
                        onChange={(e) => setArea(e.target.value)}
                    >
                        <option value="0">TODOS</option>
                        {MACRO_AREAS.map(a => (
                            <option key={a.id} value={a.id}>
                                {a.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label>Estado:</label>
                    <select
                        value={estado}
                        onChange={(e) => setEstado(e.target.value)}
                    >
                        <option value="activos">ACTIVOS</option>
                        <option value="cesados">CESADOS</option>
                    </select>
                </div>
            </div>

            <div className="mb-4">
                <ButtonSearch onClick={fetchData} isLoading={loading} />
            </div>

            <div className="table-container" style={{ marginTop: '30px' }}>
                <table className="table-reporte-split">
                    <thead>
                        <tr>
                            <th>GRÁFICO</th>
                            <th>DETALLES</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={{ verticalAlign: 'middle', padding: '40px 20px' }}>
                                {data.length > 0 ? (
                                    <div className="flex justify-center items-center w-full">
                                        <div className="donut-chart-container relative">
                                            <DonutChart
                                                data={data}
                                                category="total"
                                                index="name"
                                                colors={colors}
                                                variant="donut"
                                                showLabel={false}
                                                className="h-full w-full"
                                                customTooltip={CustomTooltip}
                                            />
                                            <div className="donut-center-text">
                                                <p className="total-number">{totalColaboradores}</p>
                                                <p className="total-label">Colaboradores</p>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-20 text-gray-400 uppercase tracking-widest text-sm">No hay datos</div>
                                )}
                            </td>
                            <td style={{ verticalAlign: 'top', padding: '40px 20px' }}>
                                {data.length > 0 ? (
                                    <div className="detalles-perfiles-container">
                                        <div className="max-h-[350px] overflow-y-auto pr-2 custom-legend-container">
                                            {data.map((item, idx) => (
                                                <div key={idx} className="perfil-item">
                                                    <div className="perfil-name">
                                                        <div 
                                                            className="dot" 
                                                            style={{ backgroundColor: tailwindColorMap[colors[idx % colors.length]] || colors[idx % colors.length] }}
                                                        ></div>
                                                        <span>{item.name}</span>
                                                    </div>
                                                    <span className="perfil-total">{item.total}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-20 text-gray-400 uppercase tracking-widest text-sm">Sin detalles disponibles</div>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ReporteColaboradores;
